import { useState } from 'react';
import { Grid, Stack, Typography, styled, alpha, Tooltip, TextField, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Search } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import numeral from 'numeral';
import {
  // Download,
  Eye,
} from 'react-feather';
import { useSearchParams } from 'react-router-dom';
import { DateRange, DateRangePicker } from '@adamftd/x-date-pickers-pro';

// Components
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import Table from 'src/components/Table';
import PopoverAction from 'src/components/Table/PopoverAction';
import { UserIcon } from 'src/components/Icons';
import PlatformIcon from 'src/components/Icons/PlatformIcon';

// Utilities
import { useQueryParam } from 'src/hooks/useQueryParam';
import { useQueryListInvoices } from 'src/services/queries';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import { IPaymentHistory } from 'src/utils/typings/response/payment';
import { TypeTabSetting } from 'src/features/Setting/config';
import { getInvoiceStatus } from 'src/components/InvoiceTemplate/config';

const StyledIcon = styled(UserIcon)(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.07),
  color: theme.palette.primary.light,
  padding: theme.spacing(0.5),
  borderRadius: '100%',
  fontSize: 22,
}));

const PaymentHistory = () => {
  const { t } = useTranslation();
  const [, setSearchParam] = useSearchParams();
  const billingId = useQueryParam('billingId');
  const [params, setParams] = useState({
    limit: LIMIT_GET_LIST,
    offset: 0,
  });
  const { data, isFetching } = useQueryListInvoices(params);

  const formik = useFormik({
    initialValues: {
      billing_id: '',
      from_date: dayjs(),
      to_date: dayjs(),
    },
    validationSchema: Yup.object().shape({
      billing_id: Yup.string().required('This field is required').max(255),
      from_date: Yup.mixed(),
      to_date: Yup.mixed(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(false);
    },
  });

  const deviceMangerColumn: ConfigCell<IPaymentHistory>[] = [
    {
      disablePadding: false,
      id: 'id',
      label: t('payment_history.table.billing_id'),
      width: 300,
      cell: (data: IPaymentHistory) => {
        return (
          <Typography color="secondary.main" variant="body2" fontWeight={500}>
            #{(data.invoice_id || '').toUpperCase()}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'email',
      label: t('payment_history.table.billed_to'),
      cell: (data: IPaymentHistory) => {
        return (
          <Stack direction={'row'} gap={1}>
            <StyledIcon />
            <Tooltip title={data.email}>
              <Typography color={'text.secondary'} variant="body2" noWrap>
                {data.email}
              </Typography>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'status',
      label: t('payment_history.table.status'),
      cell: (data: IPaymentHistory) => {
        return getInvoiceStatus(data.status);
      },
    },
    {
      disablePadding: false,
      id: 'plan_name',
      label: t('payment_history.table.plan_name'),
      cell: (data: IPaymentHistory) => {
        return (
          <Typography color={'text.secondary'} variant="body2">
            {data.plan_name}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'created_at',
      label: t('payment_history.table.created_at'),
      cell: (data: IPaymentHistory) => {
        return (
          <Typography color={'text.secondary'} variant="body2">
            {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, data.payment_date)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'payment_method',
      label: t('payment_history.table.payment_method'),
      align: 'center',
      cell: (data: IPaymentHistory) => {
        return <PlatformIcon id={data.payment_method.toLowerCase()} />;
      },
    },
    {
      disablePadding: false,
      id: 'amount',
      label: t('payment_history.table.amount'),
      cell: (data: IPaymentHistory) => {
        return (
          <Typography color={'text.secondary'} variant="body2">
            ${numeral(data.amount).format('0,0.00')}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'action',
      label: 'Action',
      cell: (data: IPaymentHistory) => {
        return (
          <PopoverAction
            primaryTypographyProps={{ color: 'suggestion' }}
            listAction={[
              {
                icon: <IconFeather muiProps={{ fontSize: 'small', color: 'inherit' }} icon={<Eye />} />,
                text: t('payment_history.table.view_invoice'),
                action: () => setSearchParam({ tab: TypeTabSetting.payment_history, billingId: data.invoice_id }),
              },
              // {
              //   icon: <IconFeather muiProps={{ fontSize: 'small', color: 'inherit' }} icon={<Download />} />,
              //   text: t('global.download'),
              //   action: () => console.log(data),
              // },
            ]}
          />
        );
      },
    },
  ];

  return (
    <FormikForm formik={formik}>
      <Grid container gap={2} spacing={2}>
        <Grid xs={12} item>
          <Typography variant="h6">{t('payment_history.title')}</Typography>
          <Typography variant="body2" color={'text.secondary'}>
            {t('payment_history.sub_title')}
          </Typography>
        </Grid>
        {!billingId && (
          <>
            <Grid xs={12} item container spacing={3}>
              <Grid xs={8} item>
                <TextField
                  required
                  id="billing_id"
                  name="billing_id"
                  value={get(formik.values, 'billing_id', '')}
                  size="small"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={Boolean(formik.touched.billing_id && formik.errors.billing_id)}
                  helperText={Boolean(formik.touched.billing_id) && formik.errors.billing_id}
                  label={t('payment_history.filter.billing_id')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="disabled" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <DateRangePicker
                  defaultValue={[null, null]}
                  format={FORMAT_DATE_TIME.FULL_ONLY_DATE}
                  onChange={(value: DateRange<Dayjs>) => {
                    let fromTime,
                      toTime = '';
                    if (value[0]) {
                      fromTime = dayjs(value[0]).format(FORMAT_DATE_TIME.yyyy_mm_dd);
                    }
                    if (value[1]) {
                      toTime = dayjs(value[1]).format(FORMAT_DATE_TIME.yyyy_mm_dd);
                    }
                    formik.setFieldValue('from_date', fromTime);
                    formik.setFieldValue('to_date', toTime);
                  }}
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: {
                      actions: ['clear'],
                    },
                  }}
                  localeText={{
                    start: t('payment_history.filter.from_date'),
                    end: t('payment_history.filter.to_date'),
                  }}
                />
              </Grid>
            </Grid>
            <Grid xs={12} item>
              <Table
                loading={isFetching}
                onChangePage={(e) => {
                  setParams({ ...params, ...{ offset: e } });
                }}
                onChangePageSize={(e) => setParams({ ...params, ...{ limit: e } })}
                pageSize={data.page_size}
                page={data.page}
                data={getArrayDefault(data.data)}
                //   totalRecords={data?.total_records || 0}
                configColumn={deviceMangerColumn}
                dataKey={'invoice_id'}
              />
            </Grid>
          </>
        )}
      </Grid>
    </FormikForm>
  );
};

export default PaymentHistory;
